class MonadicError extends Error{constructor(m,p,e,a,c){super(m);this.name='MonadicError';this.path=p;this.expected=e;this.actual=a;this.callStack=c}};
function typeError(p,e,v){const a=v===null?'null':typeof v;const err=new MonadicError('Expected '+e+" for '"+p+"', got "+a,p,e,a);const c=globalThis.__tjs?.getConfig?.();if(c?.logTypeErrors)console.error('[TJS TypeError] '+err.message);if(c?.throwTypeErrors)throw err;return err};
function isMonadicError(v){return v instanceof Error&&v.name==='MonadicError'&&'path' in v};
const __stack=[];function pushStack(n){__stack.push(n)}function popStack(){__stack.pop()}function getStack(){return[...__stack]};
function Type(d,p,e){const t={description:d,__runtimeType:true};if(typeof p==='function'){t.check=p;t.default=e??null}else{const ex=e??p;t.default=ex;t.check=v=>{if(ex===null)return true;return typeof v===typeof ex}}return t};
const __tjs = globalThis.__tjs?.createRuntime?.() ?? {typeError,isMonadicError,pushStack,popStack,getStack,Type};
/* tjs <- component.ts */

/*#
# Web-Components

**tosijs** provides the abstract `Component` class to make defining custom-elements
easier.

- `Component` leverages the [elements](/elements/) proxy and [css](/css/) to make
defining elements very efficient.
- `Component` makes it easy to create custom-elements with
no shadowDOM but with slotting behavior so your elements are lighter weight and
easier to style.
- It solves friction points like allowing element tagNames to be changed on-the-fly to avoid registry clashes.
- It allows you to deploy `Component` classes as zero dependency [blueprints](/blueprint-loader/) functions.

## Component

To define a custom-element you can subclass `Component`, simply add the properties
and methods you want, with some help from `Component` itself, and then simply
export your new class's `elementCreator()` which is a function that defines your
new component's element and produces instances of it as needed.

```
import {Component} from 'tosijs'

class ToolBar extends Component {
  static preferredTagName = 'tool-bar'
  static shadowStyleSpec = {
    ':host': {
      display: 'flex',
      gap: '10px',
    },
  }
}

export const toolBar = ToolBar.elementCreator()
```

> **Note**: Custom elements default to `display: inline`, which often causes them to
> appear dimensionless. Unless you want this (e.g., for content-holder elements),
> set an explicit `display` value (e.g., `block`, `inline-block`, `flex`) in your
> `:host` styles.

This component is just a structural element. By default a `Component` subclass will
comprise itself and a `<slot>`. You can change this by giving your subclass its
own `content` template.

`static preferredTagName` sets the desired tag name for the custom element.
If omitted, it is derived from the class name (e.g. `ToolBar` → `tool-bar`),
but this does not survive minification. `elementCreator()` returns an
`ElementCreator` function that creates instances of the element.

See [elements](/elements/) for more information on `ElementCreator` functions.

### Component properties

#### content: Element | Element[] | () => Element | () => Element[] | null

Here's a simple example of a custom-element that simply produces a
`<label>` wrapped around `<span>` and an `<input>`. Its value is synced
to that of its `<input>` so the user doesn't need to care about how
it works internally.

```js
import { Component, elements } from 'tosijs'

class LabeledInput extends Component {
  static initAttributes = { caption: 'untitled' }
  value = ''

  content = ({label, span, input}) => label(span(), input())

  connectedCallback() {
    super.connectedCallback()
    const {input} = this.parts
    input.addEventListener('input', () => {
      this.value = input.value
    })
  }

  render() {
    super.render()
    const {span, input} = this.parts
    span.textContent = this.caption
    if (input.value !== this.value) {
      input.value = this.value
    }
  }
}

const labeledInput = LabeledInput.elementCreator()

preview.append(
  labeledInput({caption: 'A text field', value: 'some text'})
)
```

`content` is, in essence, a template for the internals of the element. By default
it's a single `<slot>` element. If you explicitly want an element with no content
you can set your subclass's content to `null` or omit any `<slot>` from its template.

By setting content to be a function that returns elements instead of a collection
of elements you can take customize elements based on the component's properties.
In particular, you can use `onXxxx` syntax sugar to bind events.

(Note that you cannot bind to xin paths reliably if your component uses a `shadowDOM`
because `xin` cannot "see" elements there. As a general rule, you need to take care
of anything in the `shadowDOM` yourself.)

##### ElementProps in content arrays

When `content` returns an array, any plain objects (ElementProps) in the array are
applied to the **host element** itself, just as they would be applied to the element
being created by `div()`, `span()`, etc. This provides a clean way to set up styles,
event handlers, classes, and bindings on the component from within `content`:

```
class MyButton extends Component {
  static preferredTagName = 'my-button'

  content = ({span}) => [
    { onClick: () => console.log('clicked!'), style: { cursor: 'pointer' } },
    span({part: 'label'}, 'Click me'),
  ]
}
```

Multiple ElementProps objects are merged (later values override earlier ones).
Only plain objects are treated as props — DOM nodes, strings, numbers, and proxied
values pass through as children.

If you'd like to see a more complex example along the same lines, look at
[form and field](https://ui.tosijs.net/form/).

##### <slot> names and the `slot` attribute

```
class MenuBar extends Component {
  static shadowStyleSpec = {
    ':host, :host > slot': {
      display: 'flex',
    },
    ':host > slot:nth-child(1)': {
      flex: '1 1 auto'
    },
  }

  content = ({slot}) => [slot(), slot({name: 'gadgets'})]
}

export menuBar = MenuBar.elementCreator()
```

One of the neat things about custom-elements is that you can give them *multiple*
`<slot>`s with different `name` attributes and then have children target a specific
slot using the `slot` attribute.

This app's layout (the nav sidebar that disappears if the app is in a narrow space, etc.)
is built using just such a custom-element.

#### `<tosi-slot>`

If you put `<slot>` elements inside a `Component` subclass that doesn't have a
shadowDOM, they will automatically be replaced with `<tosi-slot>` elements that
have the expected behavior (i.e. sucking in children in based on their `<slot>`
attribute).

`<tosi-slot>` doesn't support `:slotted` but since there's no shadowDOM, just
style such elements normally, or use `tosi-slot` as a CSS-selector.

Note that you cannot give a `<slot>` element attributes (other than `name`) so if
you want to give a `<tosi-slot>` attributes (such as `class` or `style`), create it
explicitly (e.g. using `elements.tosiSlot()`) rather than using `<slot>` elements
and letting them be switched out (because they'll lose any attributes you give them).

> The legacy name `<xin-slot>` still works but emits a deprecation warning.

Here's a very simple example:

```js
import { Component, elements } from 'tosijs'

class FauxSlotExample extends Component {
  content = ({h4, h5, tosiSlot}) => [
    h4('This is a web-component with no shadow DOM and working slots!'),
    h5('top slot'),
    tosiSlot({name: 'top'}),
    h5('middle slot'),
    tosiSlot(),
    h5('bottom slot'),
    tosiSlot({name: 'bottom'}),
  ]
}

FauxSlotExample.preferredTagName = 'faux-slot-example'
FauxSlotExample.lightStyleSpec = {
  ':host': {
    display: 'flex',
    flexDirection: 'column'
  },
  ':host h4, :host h5': {
    margin: 0,
  },
  ':host tosi-slot': {
    border: '2px solid grey'
  }
}
const fauxSlotExample = FauxSlotExample.elementCreator()

const { div } = elements

preview.append(
  fauxSlotExample(
    div({slot: 'bottom'}, 'I should be on the bottom'),
    div({slot: 'top'}, 'I should be on the top'),
    div('I should be in the middle')
  )
)
```

> ##### Background
>
> `<slot>` elements do not work as expected in shadowDOM-less components. This is
> hugely annoying since it prevents components from composing nicely unless they
> have a shadowDOM, and while the shadowDOM is great for small widgets, it's
> terrible for composite views and breaks `tosijs`'s bindings (inside the shadow
> DOM you need to do data- and event- binding manually).

#### styleNode: HTMLStyleElement

`styleNode` is the `<style>` element that will be inserted into the element's
`shadowRoot`.

If a `Component` subclass has no `styleNode`, no `shadowRoot` will be
created. This reduces the memory and performance cost of the element.

This is to avoid the performance/memory costs associated with the `shadowDOM`
for custom-elements with no styling.

##### Notes

Styling custom-elements can be tricky, and it's worth learning about
how the `:host` and `:slotted()` selectors work.

It's also very useful to understand how CSS-Variables interact with the
`shadowDOM`. In particular, CSS-variables are passed into the `shadowDOM`
when other CSS rules are not. You can use css rules to modify css-variables
which will then penetrate the `shadowDOM`.

#### refs: {[key:string]: Element | undefined}

    render() {
      super.render() // see note
      const {span, input} = this.parts
      span.textContent = this.caption
      if (input.value !== this.value) {
        input.value = this.value
      }
    }

> **Note**: For form-associated components, `super.render()` syncs the form value
> automatically when the value changes. Always call `super.render()` if you override
> `render()` in a form-associated component.
>
> It is *necessary* to call `super.connectedCallback`, `super.disconnectedCallback`,
> `super.render()` (for form-associated), and `super()` in the `constructor()`
> should you override them.

`this.parts` returns a proxy that provides elements conveniently and efficiently. It
is intended to facilitate access to static elements (it memoizes its values the
first time they are computed).

`this.parts.foo` will return a content element with `data-ref="foo"`. If no such
element is found it tries it as a css selector, so `this.parts['.foo']` would find
a content element with `class="foo"` while `this.parts.h1` will find an `<h1>`.

`this.parts` will also remove a `data-ref` attribute once it has been used to find
the element. This means that if you use all your refs in `render` or `connectedCallback`
then no trace will remain in the DOM for a mounted element.

### Component properties

#### content: ((elements: ElementsProxy) => ContentType) | null | ContentType = slot()

A component's content `property` can either be static content (it defaults to being a `<slot>` element) or an arrow function
that creates the basic content of the element on hydration. Static content will be deep-cloned.

By using an arrow function the content created can refer to the custom-element's properties and attributes (and this occurs post-initialization). This also means you can bind event-handlers in the component (which should also be arrow functions unless they don't need to refer to the element)

Because a `content` function is passed the `elements` proxy, you can easily destructure any element creators you need:

```
content = ({div}) => div('hello world')
```

`ContentType` can be an HTMLElement or an array of elements.

> Note that if a component does not use the shadowDOM, its `<slot>` elements will be replaced with `<tosi-slot>` elements.
> This allows composition to work as expected without requiring the shadow DOM.

### Component static properties

#### static initAttributes: Record<string, any>

Declares attributes that should be watched and synced with properties. The keys are
property names (camelCase), and the values are the defaults which also determine the type.

    import { Component } from 'tosijs'

    class MyWidget extends Component {
      static initAttributes = {
        caption: '',      // string attribute
        count: 0,         // number attribute (auto-parsed)
        disabled: false,  // boolean attribute (presence/absence)
      }

      render() {
        // this.caption, this.count, this.disabled are automatically available
        // and synced with HTML attributes
      }
    }

This replaces both the old `initAttributes()` method call AND the instance property
declarations. A single static object now defines which properties are attributes,
their default values, and their types:

- **All-in-one**: Attributes, defaults, and types defined in one place
- **Declarative**: No constructor needed
- **Type inference**: Default values determine parsing (boolean attributes just work)

##### Attribute Types

- **string** (default `''`): Attribute value used as-is
- **number** (default `0`): Attribute value parsed with `parseFloat()`
- **boolean** (default `false`): Presence means `true`, absence means `false`

For non-attribute properties (e.g. objects), just declare them as regular instance
properties on your class.

##### Migration from initAttributes()

Old (deprecated):

    class MyComponent extends Component {
      caption = ''
      count = 0

      constructor() {
        super()
        this.initAttributes('caption', 'count')
      }
    }

New:

    class MyComponent extends Component {
      static initAttributes = { caption: '', count: 0 }
    }

### Component methods

#### queueRender(triggerChangeEvent = false): void

Uses `requestAnimationFrame` to queue a call to the component's `render` method. If
called with `true` it will also trigger a `change` event.

#### private initValue(): void

**Don't call this!** Sets up expected behavior for an `HTMLElement` with
a value (i.e. triggering a `change` events and `render` when the `value` changes).

#### private hydrate(): void

**Don't call this** Appends `content` to the element (or its `shadowRoot` if it has a `styleNode`)

#### connectedCallback(): void

If the class has an `onResize` handler then a ResizeObserver will trigger `resize`
events on the element when its size changes and `onResize` will be set up to respond
to `resize` events.

Also, if the subclass has defined `value`, calls `initValue()`.

`connectedCallback` is a great place to attach **event-handlers** to elements in your component.

Be sure to call `super.connectedCallback()` if you implement `connectedCallback` in the subclass.

#### disconnectedCallback(): void

Be sure to call `super.disconnectedCallback()` if you implement `disconnectedCallback` in the subclass.

#### render(): void

Be sure to call `super.render()` if you implement `render` in the subclass.

### Component static properties

#### Component.elements

    const {label, span, input} = Component.elements

This is simply provided as a convenient way to get to [elements](/elements/)

#### static formAssociated: boolean

Set `static formAssociated = true` in your subclass to enable form participation
via `ElementInternals`. When true, the component will have `this.internals` available
for form integration, validation, ARIA properties, and custom states.

Form-associated components are automatically made focusable (`tabindex="0"`) unless
you explicitly set a different `tabindex`. This is required for form validation to
work correctly (the browser needs to focus invalid elements).

See [web-component-validation](/form-validation/) for the complete validation
API documentation, including:

- Validation methods (`checkValidity()`, `reportValidity()`, `setValidity()`)
- Automatic validation against HTML attributes (`required`, `minlength`, `maxlength`, `pattern`)
- Form lifecycle callbacks (`formResetCallback`, `formDisabledCallback`, `formStateRestoreCallback`)
- Custom states via `this.internals.states`
- Complete examples

#### value property

**If your component has a `value`, it should behave like an `<input>`.**

The `value` property is special in Component. It is NOT an attribute - it's a property
that can be *initialized* from an attribute. Here's what you need to know:

1. **Declare it with a default**: Simply assign a non-undefined default (e.g., `value = ''`)
2. **Initialization**: If a `value` attribute is present, it initializes the property (as a string)
3. **Setting value**: You can set it to any type directly (e.g., objects, arrays)
4. **Change events**: When `value` changes, a `change` event is automatically dispatched
5. **Auto-render**: When `value` changes, `render()` is automatically called
6. **Computed values**: If your value is computed, call `queueRender(true)` to trigger change + render

**Do NOT put `value` in `static initAttributes`** - it will be rejected with a warning.
The Component class handles `value` specially to provide form-like behavior automatically.

#### adoptedCallback

The `adoptedCallback` lifecycle method is called when a component is moved to a different
document, such as into or out of an iframe. Subclasses can implement this directly.

```js
import { Component, elements } from 'tosijs'

class AdoptableWidget extends Component {
  docCount = 0

  content = ({span}) => span({part: 'info'})

  adoptedCallback() {
    this.docCount++
    this.queueRender()
  }

  render() {
    this.parts.info.textContent = `Adopted ${this.docCount} time(s). Document: ${this.ownerDocument.title || 'untitled'}`
  }
}

AdoptableWidget.preferredTagName = 'adoptable-widget'
const adoptableWidget = AdoptableWidget.elementCreator()
const {iframe, button, div, span} = elements

const widget = adoptableWidget()
const widgetSlot = span({class: 'widget-slot'}, widget)
const frame = iframe()
const moveBtn = button('Move to iframe')
const backBtn = button('Move back')

moveBtn.addEventListener('click', () => {
  frame.contentDocument.body.append(frame.contentDocument.adoptNode(widget))
})
backBtn.addEventListener('click', () => {
  widgetSlot.append(document.adoptNode(widget))
})

preview.append(widgetSlot, div(moveBtn, backBtn), frame)
```
```css
.preview .widget-slot {
  display: block;
  min-height: 40px;
  border: 2px dashed #888;
  margin-bottom: 10px;
}
.preview adoptable-widget {
  display: block;
  padding: 10px;
  background: #666;
  color: white;
}
.preview > div { display: flex; gap: 8px; margin-bottom: 10px; }
.preview iframe {
  width: 100%;
  height: 60px;
  border: 2px dashed #888;
  background: #fff;
}
```

### Component static properties

#### `static preferredTagName?: string`

Sets the desired tag name for the custom element. If omitted, it is derived
from the class name (e.g. `ToolBar` → `tool-bar`), but this does **not** survive
minification. If the tag is already in use, a unique anonymous tag is generated.

#### `static shadowStyleSpec?: XinStyleSheet`

Styles injected into the component's shadow DOM as a `<style>` element.
Setting this property causes the component to use shadow DOM.

#### `static lightStyleSpec?: XinStyleSheet`

Global styles appended to `document.head` when the first instance is inserted
in the DOM. `:host` selectors are automatically rewritten to the tag name, e.g.:

    class ToolBar extends Component {
      static preferredTagName = 'tool-bar'
      static lightStyleSpec = {
        ':host': {
          display: 'flex',
          padding: 'var(--toolbar-padding, 0 8px)',
          gap: '4px'
        }
      }
    }

produces `tool-bar { display: flex; ... }` in a global `<style>` element.

#### `static extends?: string`

For customized built-in elements. Passed as `{ extends }` to `customElements.define()`.

### Component static methods

#### Component.elementCreator(): ElementCreator

    export const toolBar = ToolBar.elementCreator()

Returns a function that creates the custom-element. Registration uses
`preferredTagName`, `lightStyleSpec`, `shadowStyleSpec`, and `extends`
from the class's static properties.

`elementCreator` is memoized and only generated once.

> **Deprecated:** Passing `{ tag, styleSpec, extends }` as options to
> `elementCreator()` still works but emits deprecation warnings.
> Use the static properties instead.

## Examples

[tosijs-ui](https://ui.tosijs.net) is a component library built using this `Component` class
that provides the essential additions to standard HTML elements needed to build many
user-interfaces.

- [live-example](https://ui.tosijs.net/live-example/) uses multiple named slots to implement
  powers the interactive examples used for this site.
- [side-nav](https://ui.tosijs.net/side-nav/) implements the sidebar navigation
  used on this site.
- [data-table](https://ui.tosijs.net/data-table/) implements virtualized tables
  with resizable, reorderable, sortable columns that can handle more data
  than you're probably willing to load.
- [form and field](https://ui.tosijs.net/form/) allow you to
  quickly create forms that leverage all the built-in functionality of `<input>`
  elements (including powerful validation) even for custom-fields.
- [markdown-viewer](https://ui.tosijs.net/markdown-viewer/) uses `marked` to render
  markdown.
- [babylon-3d](https://ui.tosijs.net/babylon-3d/) lets you easily embed 3d scenes
  in your application using [babylonjs](https://babylonjs.com/)
*/

import { css } from './css'

import { XinStyleSheet } from './css-types'

import { deepClone } from './deep-clone'

import { appendContentToElement, dispatch, resizeObserver } from './dom'

import { ElementsProxy } from './elements-types'

import { elements, elementSet } from './elements'

import { tosiPath } from './metadata'

import { validateAgainstConstraints } from './form-validation'

import { camelToKabob, kabobToCamel } from './string-case'

import { ElementCreator, ContentType, PartsMap } from './xin-types'

import { warnDeprecated } from './metadata'

let anonymousElementCount = 0;

/* line 590 */
function anonElementTag() {
    return `custom-elt${(anonymousElementCount++).toString(36)}`;
}
anonElementTag.__tjs = {
  "params": {},
  "returns": {
    "type": {
      "kind": "string"
    }
  },
  "unsafeReturn": true,
  "source": "component.ts:590"
}

let instanceCount = 0;

let legacyAttributeObserver = null;

/* line 598 */
function getLegacyAttributeObserver() {
    if (legacyAttributeObserver === null) {
        legacyAttributeObserver = new MutationObserver((mutationsList) => {
            const componentsToRender = new Set();
            for (const mutation of mutationsList) {
                if (mutation.type === 'attributes' &&
                    mutation.target instanceof Component) {
                    const component = mutation.target;
                    const attrName = kabobToCamel(mutation.attributeName);
                    if (component._legacyTrackedAttrs?.has(attrName)) {
                        componentsToRender.add(component);
                    }
                }
            }
            for (const component of componentsToRender) {
                component.queueRender(false);
            }
        });
    }
    return legacyAttributeObserver;
}
getLegacyAttributeObserver.__tjs = {
  "params": {},
  "returns": {
    "type": {
      "kind": "object",
      "shape": {}
    }
  },
  "unsafeReturn": true,
  "source": "component.ts:598"
}

const ElementCreatorOptions = Type('ElementCreatorOptions', undefined, { tag: '', styleSpec: null })

const globalStyleSheets = {};

/* line 631 */
/* TODO: TS types degraded — styleSpec: XinStyleSheet */
function setGlobalStyle(tagName, styleSpec) {
  try {
  __tjs.pushStack('component.ts:640:setGlobalStyle');
  if (tagName instanceof Error) return tagName;
  if (styleSpec instanceof Error) return styleSpec;
  if (typeof tagName !== 'string') return __tjs.typeError('component.ts:640:setGlobalStyle.tagName', 'string', tagName);

    const existing = globalStyleSheets[tagName];
    const processed = css(styleSpec)
        .replace(/:host\(([^)]+)\)/g, `${tagName}$1`)
        .replace(/:host\b/g, tagName);
    globalStyleSheets[tagName] = existing
        ? existing + '\n' + processed
        : processed;

  } finally { __tjs.popStack(); }
}
setGlobalStyle.__tjs = {
  "params": {
    "tagName": {
      "type": {
        "kind": "string"
      },
      "required": true,
      "default": null
    },
    "styleSpec": {
      "type": {
        "kind": "any"
      },
      "required": false
    }
  },
  "source": "component.ts:640"
}

/* line 641 */
function insertGlobalStyles(tagName) {
  try {
  __tjs.pushStack('component.ts:641:insertGlobalStyles');
  if (tagName instanceof Error) return tagName;
  if (typeof tagName !== 'string') return __tjs.typeError('component.ts:641:insertGlobalStyles.tagName', 'string', tagName);

    if (globalStyleSheets[tagName]) {
        document.head.append(elements.style({ id: tagName + '-component' }, globalStyleSheets[tagName]));
    }
    delete globalStyleSheets[tagName];

  } finally { __tjs.popStack(); }
}
insertGlobalStyles.__tjs = {
  "params": {
    "tagName": {
      "type": {
        "kind": "string"
      },
      "required": true,
      "default": null
    }
  },
  "source": "component.ts:641"
}

export class Component extends HTMLElement {
  static elements = elements;
  static _elementCreator
  static initAttributes
  static formAssociated
  static preferredTagName
  static shadowStyleSpec
  static lightStyleSpec
  static extends
  internals
  get validity() {
    return this.internals?.validity;
}
  get validationMessage() {
    return this.internals?.validationMessage ?? '';
}
  get willValidate() {
    return this.internals?.willValidate ?? false;
}
  checkValidity() {
    return this.internals?.checkValidity() ?? true;
}
  reportValidity() {
    return this.internals?.reportValidity() ?? true;
}
  setCustomValidity(message = '') {
    if (this.internals) {
        if (message) {
            this.internals.setValidity({ customError: true }, message);
        }
        else {
            this.internals.setValidity({});
        }
    }
}
  setValidity(flags,message = '' | undefined,anchor = {} | undefined) {
    this.internals?.setValidity(flags, message, anchor);
}
  setFormValue(value, state) {
    this.internals?.setFormValue(value, state);
}
  static get observedAttributes() {
    const initAttrs = this.initAttributes;
    if (initAttrs) {
        return ['hidden', ...Object.keys(initAttrs).map(camelToKabob)];
    }
    return ['hidden'];
}
  instanceId
  styleNode
  static styleSpec
  static styleNode
  content = elements.slot();
  isSlotted
  static _tagName = null;
  static get tagName() {
    return this._tagName;
}
  _legacyTrackedAttrs
  _attrValues
  _valueChanged = false;
  _pendingAttrOps
  static StyleNode(styleSpec) {
    console.warn('StyleNode is deprecated, use static shadowStyleSpec instead');
    return elements.style(css(styleSpec));
}
  static elementCreator(options = {}) {
    const componentClass = this;
    if (!Object.prototype.hasOwnProperty.call(componentClass, '_elementCreator')) {
        // Deprecation warnings for options-based API
        if (options.tag !== undefined) {
            warnDeprecated('elementCreator-tag', 'Passing tag to elementCreator() is deprecated. Use static preferredTagName instead.');
        }
        if (options.styleSpec !== undefined) {
            warnDeprecated('elementCreator-styleSpec', 'Passing styleSpec to elementCreator() is deprecated. Use static lightStyleSpec instead.');
        }
        if (options.extends !== undefined) {
            warnDeprecated('elementCreator-extends', 'Passing extends to elementCreator() is deprecated. Use static extends instead.');
        }
        // Resolve tag: options.tag > static preferredTagName > camelToKabob > anon
        let tagName = options.tag ?? componentClass.preferredTagName;
        if (tagName == null) {
            if (typeof componentClass.name === 'string' &&
                componentClass.name !== '') {
                tagName = camelToKabob(componentClass.name);
                if (tagName.startsWith('-')) {
                    tagName = tagName.slice(1);
                }
            }
            else {
                tagName = anonElementTag();
            }
        }
        if (customElements.get(tagName) != null) {
            console.warn(`${tagName} is already defined`);
        }
        if (tagName.match(/\w+(-\w+)+/) == null) {
            console.warn(`${tagName} is not a legal tag for a custom-element`);
            tagName = anonElementTag();
        }
        while (customElements.get(tagName) !== undefined) {
            tagName = anonElementTag();
        }
        componentClass._tagName = tagName;
        // Resolve light style spec: options.styleSpec > static lightStyleSpec
        const lightStyleSpec = options.styleSpec ?? componentClass.lightStyleSpec;
        if (lightStyleSpec !== undefined) {
            setGlobalStyle(tagName, lightStyleSpec);
        }
        // Resolve extends: options.extends > static extends
        const extendsTag = options.extends ?? componentClass.extends;
        const defineOptions = extendsTag
            ? { extends: extendsTag }
            : undefined;
        window.customElements.define(tagName, this, defineOptions);
        componentClass._elementCreator = elements[tagName];
    }
    return componentClass._elementCreator;
}
  initAttributes(...attributeNames) {
    warnDeprecated('initAttributes', 'initAttributes() is deprecated. Use static initAttributes = { ... } instead.');
    // Track which attributes this instance is watching via legacy mechanism
    if (!this._legacyTrackedAttrs) {
        this._legacyTrackedAttrs = new Set();
    }
    for (const name of attributeNames) {
        this._legacyTrackedAttrs.add(name);
    }
    // Use shared MutationObserver instead of per-instance observer
    const observer = getLegacyAttributeObserver();
    observer.observe(this, { attributes: true });
    const attributes = {};
    const attributeValues = {};
    attributeNames.forEach((attributeName) => {
        attributes[attributeName] = deepClone(this[attributeName]);
        const attributeKabob = camelToKabob(attributeName);
        Object.defineProperty(this, attributeName, {
            enumerable: false,
            get() {
                if (typeof attributes[attributeName] === 'boolean') {
                    return this.hasAttribute(attributeKabob);
                }
                else {
                    if (this.hasAttribute(attributeKabob)) {
                        return typeof attributes[attributeName] === 'number'
                            ? parseFloat(this.getAttribute(attributeKabob))
                            : this.getAttribute(attributeKabob);
                    }
                    else if (attributeValues[attributeName] !== undefined) {
                        return attributeValues[attributeName];
                    }
                    else {
                        return attributes[attributeName];
                    }
                }
            },
            set(value) {
                if (typeof attributes[attributeName] === 'boolean') {
                    if (value !== this[attributeName]) {
                        if (value) {
                            this.setAttribute(attributeKabob, '');
                        }
                        else {
                            this.removeAttribute(attributeKabob);
                        }
                        this.queueRender();
                    }
                }
                else if (typeof attributes[attributeName] === 'number') {
                    if (value !== parseFloat(this[attributeName])) {
                        this.setAttribute(attributeKabob, value);
                        this.queueRender();
                    }
                }
                else {
                    if (typeof value === 'object' ||
                        `${value}` !== `${this[attributeName]}`) {
                        if (value === null ||
                            value === undefined ||
                            typeof value === 'object') {
                            this.removeAttribute(attributeKabob);
                        }
                        else {
                            this.setAttribute(attributeKabob, value);
                        }
                        this.queueRender();
                        attributeValues[attributeName] = value;
                    }
                }
            },
        });
    });
}
  initValue() {
    const valueDescriptor = Object.getOwnPropertyDescriptor(this, 'value');
    if (valueDescriptor === undefined ||
        valueDescriptor.get !== undefined ||
        valueDescriptor.set !== undefined) {
        return;
    }
    let value = this.hasAttribute('value')
        ? this.getAttribute('value')
        : deepClone(this.value);
    delete this.value;
    Object.defineProperty(this, 'value', {
        enumerable: false,
        get() {
            return value;
        },
        set(newValue) {
            if (value !== newValue) {
                value = newValue;
                this._valueChanged = true;
                this.queueRender(true);
            }
        },
    });
}
  _parts
  get parts() {
    const root = this.shadowRoot != null ? this.shadowRoot : this;
    if (this._parts == null) {
        this._parts = new Proxy({}, {
            get(target, ref) {
                if (target[ref] === undefined) {
                    let element = root.querySelector(`[part="${ref}"]`);
                    if (element == null) {
                        element = root.querySelector(ref);
                    }
                    if (element == null)
                        throw new Error(`elementRef "${ref}" does not exist!`);
                    element.removeAttribute('data-ref');
                    target[ref] = element;
                }
                return target[ref];
            },
        });
    }
    return this._parts;
}
  attributeChangedCallback(name = '',_oldValue = '' | null,_newValue = '' | null) {
    // Convert kabob-case attribute to camelCase property name
    const propName = kabobToCamel(name);
    // Only queue render if this isn't a legacy-tracked attr (those use MutationObserver)
    if (!this._legacyTrackedAttrs?.has(propName)) {
        this.queueRender(false);
    }
}
  constructor() {
    super();
    instanceCount += 1;
    // The custom-elements spec forbids a constructor from "gaining
    // attributes." Property setters generated by `static initAttributes`
    // (and the legacy `initAttributes()` method) reflect to the DOM via
    // setAttribute, so any property assignment during construction —
    // class-field initializers or subclass constructor bodies — would
    // violate the spec. Install per-instance setAttribute/removeAttribute
    // overrides that queue calls. The queue drains either at the top of
    // connectedCallback (parser-upgrade path) or via a microtask
    // (createElement path), whichever fires first. Drain is idempotent.
    this._installAttributeQueue();
    // Attach ElementInternals for form-associated components
    // Only call once - attachInternals() throws if called more than once
    if (this.constructor.formAssociated &&
        typeof this.attachInternals === 'function' &&
        !this.internals) {
        this.internals = this.attachInternals();
    }
    // Set up property accessors from static initAttributes
    const initAttrs = this.constructor.initAttributes;
    if (initAttrs) {
        this._setupAttributeAccessors(initAttrs);
    }
    this.instanceId = `${this.tagName.toLocaleLowerCase()}-${instanceCount}`;
    this._value = deepClone(this.defaultValue);
}
  _installAttributeQueue() {
    const queue = [];
    this._pendingAttrOps = queue;
    this.setAttribute = (name, value) => {
        queue.push(['set', name, String(value)]);
    };
    this.removeAttribute = (name) => {
        queue.push(['remove', name]);
    };
    queueMicrotask(() => this._drainPendingAttrOps());
}
  _drainPendingAttrOps() {
    const queue = this._pendingAttrOps;
    if (queue === undefined)
        return;
    this._pendingAttrOps = undefined;
    delete this.setAttribute;
    delete this.removeAttribute;
    for (const op of queue) {
        if (op[0] === 'set') {
            // hasAttribute guard: anything already on the element (e.g. set by
            // the parser before upgrade) wins over a queued default reflection.
            if (!this.hasAttribute(op[1]))
                this.setAttribute(op[1], op[2]);
        }
        else {
            this.removeAttribute(op[1]);
        }
    }
}
  _setupAttributeAccessors(initAttrs = {}) {
    if (!this._attrValues) {
        this._attrValues = new Map();
    }
    for (const attrName of Object.keys(initAttrs)) {
        const attrKabob = camelToKabob(attrName);
        const defaultValue = initAttrs[attrName];
        // 'value' is a property, not an attribute - use Component's built-in value handling
        if (attrName === 'value') {
            console.warn(`${this.tagName}: 'value' cannot be an attribute. Use the Component value property instead.`);
            continue;
        }
        // Skip objects - attributes must be serializable (string, number, boolean)
        if (typeof defaultValue === 'object' && defaultValue !== null) {
            console.warn(`${this.tagName}: initAttributes.${attrName} is an object. Use a regular property instead.`);
            continue;
        }
        // Skip if already set up (e.g., by legacy initAttributes) or not configurable
        // Check prototype chain for non-configurable properties (e.g., 'name' on Element)
        let proto = this;
        let isNonConfigurable = false;
        while (proto) {
            const descriptor = Object.getOwnPropertyDescriptor(proto, attrName);
            if (descriptor) {
                if (!descriptor.configurable || descriptor.get || descriptor.set) {
                    isNonConfigurable = true;
                    break;
                }
                break;
            }
            proto = Object.getPrototypeOf(proto);
        }
        if (isNonConfigurable) {
            continue;
        }
        Object.defineProperty(this, attrName, {
            enumerable: false,
            get: () => {
                // DOM attribute wins over the in-memory fallback so external
                // setAttribute calls remain observable. The fallback covers the
                // window between a property assignment and its (possibly deferred)
                // DOM reflection.
                if (typeof defaultValue === 'boolean') {
                    if (this.hasAttribute(attrKabob))
                        return true;
                    if (this._attrValues.has(attrName))
                        return this._attrValues.get(attrName);
                    return false;
                }
                else if (this.hasAttribute(attrKabob)) {
                    return typeof defaultValue === 'number'
                        ? parseFloat(this.getAttribute(attrKabob))
                        : this.getAttribute(attrKabob);
                }
                else if (this._attrValues.has(attrName)) {
                    return this._attrValues.get(attrName);
                }
                else {
                    return defaultValue;
                }
            },
            set: (value) => {
                if (typeof defaultValue === 'boolean') {
                    if (value !== this[attrName]) {
                        if (value) {
                            this.setAttribute(attrKabob, '');
                        }
                        else {
                            this.removeAttribute(attrKabob);
                        }
                        this.queueRender();
                        this._attrValues.set(attrName, !!value);
                    }
                }
                else if (typeof defaultValue === 'number') {
                    if (value !== parseFloat(this[attrName])) {
                        this.setAttribute(attrKabob, value);
                        this.queueRender();
                        this._attrValues.set(attrName, value);
                    }
                }
                else {
                    if (typeof value === 'object' ||
                        `${value}` !== `${this[attrName]}`) {
                        if (value === null ||
                            value === undefined ||
                            typeof value === 'object') {
                            this.removeAttribute(attrKabob);
                        }
                        else {
                            this.setAttribute(attrKabob, value);
                        }
                        this.queueRender();
                        this._attrValues.set(attrName, value);
                    }
                }
            },
        });
    }
}
  connectedCallback() {
    // Apply any setAttribute/removeAttribute calls that were queued during
    // construction. Idempotent — if the microtask drain already ran, this
    // is a no-op.
    this._drainPendingAttrOps();
    insertGlobalStyles(this.constructor.tagName);
    this.hydrate();
    if (this.role != null)
        this.setAttribute('role', this.role);
    // Form-associated components must be focusable for validation to work
    if (this.constructor.formAssociated &&
        !this.hasAttribute('tabindex')) {
        this.setAttribute('tabindex', '0');
    }
    if (this.onResize !== undefined) {
        resizeObserver.observe(this);
        if (this._onResize == null) {
            this._onResize = this.onResize.bind(this);
        }
        this.addEventListener('resize', this._onResize);
    }
    if (this.value != null && this.getAttribute('value') != null) {
        this._value = this.getAttribute('value');
    }
    // Sync initial form value and validate for formAssociated components
    if (this.internals && this.value !== undefined) {
        this.internals.setFormValue(this.value);
        this.validateValue();
    }
    this.queueRender();
}
  disconnectedCallback() {
    resizeObserver.unobserve(this);
}
  formResetCallback() {
    if (this.value !== undefined) {
        this.value = this.defaultValue ?? '';
    }
}
  formDisabledCallback(disabled = false) {
    if (disabled) {
        this.setAttribute('disabled', '');
    }
    else {
        this.removeAttribute('disabled');
    }
}
  formStateRestoreCallback(state) {
    if (this.value !== undefined && typeof state === 'string') {
        this.value = state;
    }
}
  _changeQueued = false;
  _renderQueued = false;
  queueRender(triggerChangeEvent = false) {
    if (!this._hydrated)
        return;
    if (!this._changeQueued)
        this._changeQueued = triggerChangeEvent;
    if (!this._renderQueued) {
        this._renderQueued = true;
        requestAnimationFrame(() => {
            // TODO add mechanism to allow component developer to have more control over
            // whether input vs. change events are emitted
            if (this._changeQueued) {
                dispatch(this, 'change');
                // Sync form value for formAssociated components
                if (this.internals && this.value !== undefined) {
                    this.internals.setFormValue(this.value);
                }
            }
            this._changeQueued = false;
            this._renderQueued = false;
            this.render();
        });
    }
}
  _hydrated = false;
  hydrate() {
    if (!this._hydrated) {
        this.initValue();
        const cloneElements = typeof this.content !== 'function';
        let _content = typeof this.content === 'function'
            ? this.content(elements)
            : this.content;
        if (Array.isArray(_content)) {
            const hostProps = {};
            _content = _content.filter((item) => {
                if (item instanceof Node ||
                    typeof item === 'string' ||
                    typeof item === 'number' ||
                    tosiPath(item)) {
                    return true;
                }
                Object.assign(hostProps, item);
                return false;
            });
            for (const key of Object.keys(hostProps)) {
                elementSet(this, key, hostProps[key]);
            }
        }
        const ctor = this.constructor;
        const shadowStyle = ctor.shadowStyleSpec ?? ctor.styleSpec;
        if (ctor.styleSpec && !ctor.shadowStyleSpec) {
            warnDeprecated('static-styleSpec', 'static styleSpec is deprecated. Use static shadowStyleSpec instead.');
        }
        let { styleNode } = ctor;
        if (shadowStyle) {
            styleNode = ctor.styleNode = elements.style(css(shadowStyle));
            delete ctor.styleNode;
        }
        if (this.styleNode) {
            console.warn(this, 'styleNode is deprecated, use static shadowStyleSpec instead');
            styleNode = this.styleNode;
        }
        if (styleNode) {
            const shadow = this.attachShadow({ mode: 'open' });
            shadow.appendChild(styleNode.cloneNode(true));
            appendContentToElement(shadow, _content, cloneElements);
        }
        else if (_content !== null) {
            const existingChildren = Array.from(this.childNodes);
            appendContentToElement(this, _content, cloneElements);
            this.isSlotted =
                this.querySelector('slot,tosi-slot,xin-slot') !== undefined;
            const slots = Array.from(this.querySelectorAll('slot'));
            if (slots.length > 0) {
                slots.forEach(TosiSlot.replaceSlot);
            }
            if (existingChildren.length > 0) {
                const slotMap = { '': this };
                Array.from(this.querySelectorAll('tosi-slot,xin-slot')).forEach((slot) => {
                    slotMap[slot.name] = slot;
                });
                existingChildren.forEach((child) => {
                    const defaultSlot = slotMap[''];
                    const destSlot = child instanceof Element ? slotMap[child.slot] : defaultSlot;
                    (destSlot !== undefined ? destSlot : defaultSlot).append(child);
                });
            }
        }
        this._hydrated = true;
    }
}
  render() {
    // Sync form value and validate when value actually changed
    if (this._valueChanged && this.internals && this.value !== undefined) {
        this.internals.setFormValue(this.value);
        this.validateValue();
    }
    this._valueChanged = false;
}
  validateValue() {
    if (!this.internals || this.value === undefined)
        return;
    const value = typeof this.value === 'string' ? this.value : String(this.value);
    validateAgainstConstraints(this, value);
}
}

const SlotParts = Type('SlotParts', undefined, { slotty: {} })

class TosiSlot extends Component {
  static preferredTagName = 'tosi-slot';
  static initAttributes = { name: '' }
  content = null;
  static replaceSlot(slot = {}) {
    const _slot = document.createElement('tosi-slot');
    if (slot.name !== '') {
        _slot.setAttribute('name', slot.name);
    }
    slot.replaceWith(_slot);
}
}

export const tosiSlot = TosiSlot.elementCreator();

class XinSlot extends Component {
  static preferredTagName = 'xin-slot';
  static initAttributes = { name: '' }
  content = null;
  constructor() {
    super();
    warnDeprecated('xin-slot', '<xin-slot> is deprecated. Use <tosi-slot> instead.');
}
  static replaceSlot = TosiSlot.replaceSlot;
}

export const xinSlot = XinSlot.elementCreator();